import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  memo,
} from "react";
import { useNavigate } from "react-router-dom";
import {
  NOTIFICATION_STATUS,
  disablePushNotifications,
  getNotificationSnapshot,
  requestAndEnablePushNotifications,
  restorePushSubscriptionIfAllowed,
} from "../../lib/pwa/pushClient";
import {
  User,
  Users,
  Building2,
  Bell,
  LogOut,
  Pencil,
  Loader2,
  Save,
  KeyRound,
  Eye,
  EyeOff,
  CheckCircle2,
  XCircle,
  Gift,
  Copy,
  Share2,
  RefreshCw,
  Trash2,
  MessageCircle,
  Send,
  X,
  Settings,
  ChevronRight,
  ArrowLeft,
} from "lucide-react";

import AppScreenShell from "../../components/layout/AppScreenShell";
import Banner from "../../components/layout/Banner";

import { supabase } from "../../supabaseClient";
import { getOrCreateUserProfile } from "../../lib/account/profile";
import { signalBusinessProfileUpdated } from "../../hooks/useBusinessGate";
import {
  BUSINESS_SETUP_OPTIONS,
  TRADING_DURATION_OPTIONS,
  EMPLOYEE_COUNT_OPTIONS_V3,
  INDUSTRY_OPTIONS_V3,
  PROVINCE_OPTIONS_V3,
  ANNUAL_REVENUE_OPTIONS_V3,
  FINANCE_TRACKING_OPTIONS_V3,
  BUSINESS_BANKING_OPTIONS,
  GWIN_FOCUS_OPTIONS,
  LEARNING_DAY_OPTIONS_V3,
  CHECK_IN_TIME_OPTIONS,
} from "../../hooks/useProfileGateDraft";
import {
  buildReferralInviteLink,
  getMyReferralSummary,
} from "../../lib/referrals/referrals";
import { recordProfileAuditEvent } from "../../lib/account/profileAudit";
import DeleteAccountFlow from "../../components/account/DeleteAccountFlow";
import PrivacyPermissionsSection from "../../components/consent/PrivacyPermissionsSection";
import GwinBugReportPanel from "../../components/support/GwinBugReportPanel";
import PartnerFinancialSharingSection from "../../components/consent/PartnerFinancialSharingSection";
import {
  fetchMyLearnerSupportContacts,
  sendLearnerSupportRequest,
} from "../../lib/account/learnerSupport";


const PROFILE_SUNGLASSES_SRC =
  "https://kwbgrjakobarqelunebd.supabase.co/storage/v1/object/public/pictures/MVP4/In-App/Gwin-Sunglasses.png";

const PROFILE_BOTTOM_PENGUIN_SRC =
  "https://kwbgrjakobarqelunebd.supabase.co/storage/v1/object/public/pictures/MVP4/Public%20Pages/Landing/gwin-penguin.png";

const NO_BUSINESS_OPTION =
  "I don't have a business but I want to start one the right way";

const BUSINESS_FIELDS = [
  "business_name", "business_setup_type", "trading_duration_range",
  "employee_count_range", "industry", "province", "annual_revenue_range",
  "finance_tracking_method", "business_banking_type",
];
const LEARNER_FIELDS = ["business_priorities", "learning_days", "reminder_times"];
const LEGAL_NATURE_MAP = {
  "Pty Ltd": "Company",
  "Sole proprietor": "Sole Prop",
  Partnership: "Partnership",
};

const PASSWORD_RULES = [
  {
    key: "length",
    label: "At least 8 characters",
    test: (s) => s.length >= 8,
  },
  {
    key: "upper",
    label: "At least 1 uppercase letter (A-Z)",
    test: (s) => /[A-Z]/.test(s),
  },
  {
    key: "lower",
    label: "At least 1 lowercase letter (a-z)",
    test: (s) => /[a-z]/.test(s),
  },
  {
    key: "number",
    label: "At least 1 number (0-9)",
    test: (s) => /\d/.test(s),
  },
  {
    key: "special",
    label: "At least 1 special character (!@#$...)",
    test: (s) => /[^A-Za-z0-9]/.test(s),
  },
];

const USER_AUDIT_FIELDS = ["full_name", "mobile_number", "email"];

const BUSINESS_AUDIT_FIELDS = BUSINESS_FIELDS;

function cleanAuditValue(value) {
  if (value === null || typeof value === "undefined") return null;

  const cleaned = String(value).trim();

  return cleaned || null;
}

function pickAuditValues(source, fields) {
  return fields.reduce((acc, field) => {
    acc[field] = cleanAuditValue(source?.[field]);
    return acc;
  }, {});
}

function changedFieldsBetween(beforeValues, afterValues, fields) {
  return fields.filter((field) => {
    return (
      cleanAuditValue(beforeValues?.[field]) !==
      cleanAuditValue(afterValues?.[field])
    );
  });
}

/* ---------- Notifications helpers ---------- */
const NOTIF_ENABLE_PENDING_KEY = (uid) => `notif_enable_pending_${uid}`;

function setNotifPending(uid, value) {
  try {
    if (!uid) return;

    localStorage.setItem(
      NOTIF_ENABLE_PENDING_KEY(uid),
      value ? "true" : "false"
    );
  } catch {}
}
function getNotifPending(uid) {
  try {
    if (!uid) return false;

    return localStorage.getItem(NOTIF_ENABLE_PENDING_KEY(uid)) === "true";
  } catch {
    return false;
  }
}

function getNotifPermission() {
  if (typeof window === "undefined") return "unsupported";
  if (!("Notification" in window)) return "unsupported";

  return Notification.permission;
}

function isIOS() {
  if (typeof navigator === "undefined") return false;

  const userAgent = navigator.userAgent || "";

  return (
    /iPhone|iPad|iPod/i.test(userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
  );
}

function isStandalone() {
  if (typeof window === "undefined") return false;

  return (
    window.matchMedia?.("(display-mode: standalone)")?.matches ||
    window.navigator.standalone === true
  );
}

function notifLabel(permission, prefEnabled) {
  if (permission === "unsupported") return "Unsupported";
  if (prefEnabled === undefined) return "Loading…";
  if (prefEnabled === null) return "Not set";
  if (permission === "denied") return "Blocked";
  if (permission === "granted" && prefEnabled) return "On";

  return "Off";
}

function blockedStepsText() {
  return (
    "You blocked notifications in your browser. " +
    "Open your browser’s site settings for this website (usually a lock/tune icon next to the address bar) → " +
    "Site permissions / Permissions → Notifications → Allow. " +
    "If you previously blocked it, remove this site from the browser’s blocked list and refresh the page."
  );
}

async function copyTextToClipboard(text) {
  if (!text) return false;

  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {}

  try {
    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.setAttribute("readonly", "");
    textarea.style.position = "fixed";
    textarea.style.left = "-9999px";
    textarea.style.top = "0";

    document.body.appendChild(textarea);
    textarea.select();

    const copied = document.execCommand("copy");

    document.body.removeChild(textarea);

    return copied;
  } catch {
    return false;
  }
}

export default function Profile() {
  const navigate = useNavigate();

  const [authUser, setAuthUser] = useState(null);
  const [userInfo, setUserInfo] = useState(null);
  const [businessInfo, setBusinessInfo] = useState(undefined);

  const [loading, setLoading] = useState(true);

  const [error, setError] = useState("");
  const [savingUser, setSavingUser] = useState(false);
  const [savingBiz, setSavingBiz] = useState(false);
  const [userSaved, setUserSaved] = useState(false);
  const [bizSaved, setBizSaved] = useState(false);

  const [passwordForm, setPasswordForm] = useState({
    newPassword: "",
    confirmPassword: "",
  });

  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [passwordSaved, setPasswordSaved] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [passwordMessage, setPasswordMessage] = useState("");

  const [userForm, setUserForm] = useState({
    full_name: "",
    mobile_number: "",
    email: "",
  });

  const [bizForm, setBizForm] = useState({
    business_name: "", business_setup_type: "", trading_duration_range: "",
    employee_count_range: "", industry: "", province: "",
    annual_revenue_range: "", finance_tracking_method: "", business_banking_type: "",
  });
  const [learnerInfo, setLearnerInfo] = useState(null);
  const [learnerForm, setLearnerForm] = useState({
    business_priorities: [], learning_days: [], reminder_times: [],
  });
  const [savingLearner, setSavingLearner] = useState(false);
  const [learnerSaved, setLearnerSaved] = useState(false);

  const [uid, setUid] = useState(null);
  const [notifPermission, setNotifPermission] = useState(getNotifPermission());
  const [notifPrefEnabled, setNotifPrefEnabled] = useState(undefined);
  const [notifBusy, setNotifBusy] = useState(false);
  const [notifMsg, setNotifMsg] = useState("");
  const [deviceSubscribed, setDeviceSubscribed] = useState(false);

  const [supportContacts, setSupportContacts] = useState([]);
  const [supportLoading, setSupportLoading] = useState(true);
  const [referralSummary, setReferralSummary] = useState(null);
  const [referralLoading, setReferralLoading] = useState(false);
  const [referralError, setReferralError] = useState("");
  const [referralCopied, setReferralCopied] = useState(false);
  const [referralShareMessage, setReferralShareMessage] = useState("");
  const [deleteAccountOpen, setDeleteAccountOpen] = useState(false);
  const [activeProfilePanel, setActiveProfilePanel] = useState("menu");

  const needsInstallNote = useMemo(() => isIOS() && !isStandalone(), []);

  const referralInviteLink = useMemo(() => {
    return buildReferralInviteLink(referralSummary?.code);
  }, [referralSummary?.code]);

  const loadReferralSummary = useCallback(async () => {
    setReferralLoading(true);
    setReferralError("");

    try {
      const summary = await getMyReferralSummary(supabase);
      setReferralSummary(summary || null);
    } catch (e) {
      console.error("loadReferralSummary error:", e);

      setReferralError(
        e?.message || "Could not load your referral progress yet."
      );
    } finally {
      setReferralLoading(false);
    }
  }, []);

  const copyReferralLink = useCallback(async () => {
    setReferralCopied(false);
    setReferralShareMessage("");

    if (!referralInviteLink) {
      setReferralShareMessage("No referral link is available yet.");
      return;
    }

    const copied = await copyTextToClipboard(referralInviteLink);

    if (!copied) {
      setReferralShareMessage(
        "Could not copy the link. Please copy it manually."
      );
      return;
    }

    setReferralCopied(true);
    setReferralShareMessage("Invite link copied.");

    window.setTimeout(() => {
      setReferralCopied(false);
    }, 2500);
  }, [referralInviteLink]);

  const shareReferralLink = useCallback(async () => {
    setReferralShareMessage("");

    if (!referralInviteLink) {
      setReferralShareMessage("No referral link is available yet.");
      return;
    }

    const shareText =
      "Join me on Gwin. Use my invite link to start learning business finance:";

    try {
      if (navigator.share) {
        await navigator.share({
          title: "Join Gwin",
          text: shareText,
          url: referralInviteLink,
        });
                setReferralShareMessage("Invite shared.");
        return;
      }
    } catch (e) {
      if (e?.name === "AbortError") {
        return;
      }
    }

    await copyReferralLink();
  }, [copyReferralLink, referralInviteLink]);

  const auditProfileAction = useCallback(async (event) => {
    try {
      await recordProfileAuditEvent(event);
    } catch (auditError) {
      console.warn("Profile audit event could not be saved:", auditError);
    }
  }, []);

  const refreshNotifStatus = async () => {
    try {
      const snapshot = await getNotificationSnapshot();

      setNotifPermission(snapshot.permission);
      setNotifPrefEnabled(
        snapshot.storedStatus === NOTIFICATION_STATUS.NOT_REQUESTED
          ? null
          : snapshot.notificationsEnabled
      );
      setDeviceSubscribed(snapshot.deviceSubscribed);
    } catch (notificationError) {
      setNotifPermission(getNotifPermission());

      console.warn(
        "Could not refresh notification status:",
        notificationError?.message || notificationError
      );
    }
  };

  useEffect(() => {
    if (!uid) return;

    loadReferralSummary();
  }, [uid, loadReferralSummary]);

  useEffect(() => {
  if (!uid) return;

  let alive = true;

  async function loadSupportContacts() {
    try {
      setSupportLoading(true);

      const contacts =
        await fetchMyLearnerSupportContacts();

      if (!alive) return;

      setSupportContacts(
        Array.isArray(contacts)
          ? contacts
          : []
      );
    } catch (supportLoadError) {
      console.error(
        "Could not load learner support contacts:",
        supportLoadError
      );

      if (!alive) return;

      setSupportContacts([]);


    } finally {
      if (alive) {
        setSupportLoading(false);
      }
    }
  }

  loadSupportContacts();

  return () => {
    alive = false;
  };
}, [uid]);

  async function changePassword() {
    setPasswordSaved(false);
    setPasswordError("");
    setPasswordMessage("");

    const newPassword = passwordForm.newPassword || "";
    const confirmPassword = passwordForm.confirmPassword || "";

    const unmet = PASSWORD_RULES.filter((rule) => !rule.test(newPassword));

    if (unmet.length > 0) {
      setPasswordError("Password must meet all requirements shown below.");
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError("Passwords do not match.");
      return;
    }

    setChangingPassword(true);

    try {
      const { error: updateError } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (updateError) throw updateError;

      setPasswordForm({
        newPassword: "",
        confirmPassword: "",
      });

      await auditProfileAction({
        eventType: "password_changed",
        entityType: "auth_user",
        entityTable: "auth.users",
        entityId: authUser?.id || uid,
        changedFields: ["password"],
        beforeValues: {},
        afterValues: {
          password: "changed",
        },
        metadata: {
          source: "profile_change_password",
        },
      });

      setPasswordSaved(true);
      setPasswordMessage("Password updated successfully.");
    } catch (e) {
      console.error("changePassword error:", e);
      setPasswordError(e.message || "Could not update password.");
    } finally {
      setChangingPassword(false);
    }
  }

  const setNotificationsEnabled = async (nextEnabled) => {
    if (!uid || notifBusy) return;

    const previousValue = notifPrefEnabled;

    setNotifMsg("");
    setNotifBusy(true);

    try {
      if (!nextEnabled) {
        setNotifPending(uid, false);

        const result = await disablePushNotifications({
          allDevices: true,
        });

        setNotifPrefEnabled(false);
        setDeviceSubscribed(false);
        setNotifPermission(getNotifPermission());
        setNotifMsg(result.message);

        await auditProfileAction({
          eventType: "notification_preference_updated",
          entityType: "notification_preference",
          entityTable: "public.user",
          entityId: uid,
          changedFields: [
            "notifications_enabled",
            "notification_permission_status",
          ],
          beforeValues: {
            notifications_enabled: previousValue,
          },
          afterValues: {
            notifications_enabled: false,
            notification_permission_status:
              NOTIFICATION_STATUS.PREFERENCE_DISABLED,
          },
          metadata: {
            source: "profile_notification_settings",
            all_devices: true,
            server_cleanup_succeeded: result.ok,
            server_cleanup_error: result.serverError?.message || null,
          },
        });

        return;
      }

      const result = await requestAndEnablePushNotifications();

      setNotifPermission(result.permission || getNotifPermission());

      if (result.status === NOTIFICATION_STATUS.INSTALLATION_REQUIRED) {
        setNotifPrefEnabled(false);
        setDeviceSubscribed(false);
        setNotifPending(uid, true);
        setNotifMsg(result.message);

        navigate("/app", {
          state: {
            fromNotificationSetup: true,
            returnTo: window.location.pathname,
          },
        });

        return;
      }

      if (!result.ok) {
        const shouldRetryAfterSettingsChange =
          result.status === NOTIFICATION_STATUS.DENIED ||
          result.status === NOTIFICATION_STATUS.SUBSCRIPTION_FAILED;

        setNotifPending(uid, shouldRetryAfterSettingsChange);
        setNotifPrefEnabled(false);
        setDeviceSubscribed(false);
        setNotifMsg(result.message);

        return;
      }

      setNotifPending(uid, false);
      setNotifPrefEnabled(true);
      setDeviceSubscribed(true);
      setNotifMsg("Notifications enabled ✅ This device is registered.");

      await auditProfileAction({
        eventType: "notification_preference_updated",
        entityType: "notification_preference",
        entityTable: "public.user",
        entityId: uid,
        changedFields: [
          "notifications_enabled",
          "notification_permission_status",
        ],
        beforeValues: {
          notifications_enabled: previousValue,
        },
        afterValues: {
          notifications_enabled: true,
          notification_permission_status: result.status,
        },
        metadata: {
          source: "profile_notification_settings",
          permission_status: result.status,
          device_registered: true,
        },
      });
    } catch (notificationError) {
            setNotifMsg(
        notificationError?.message ||
          "Could not update notification settings."
      );

      await refreshNotifStatus();
    } finally {
      setNotifBusy(false);
    }
  };

  const autoRegRef = useRef(false);

  useEffect(() => {
    autoRegRef.current = false;
  }, [uid]);

  useEffect(() => {
    let alive = true;

    const restoreNotifications = async () => {
      if (!uid) return;
      if (autoRegRef.current) return;

      const pending = getNotifPending(uid);
      const shouldRestore = notifPrefEnabled === true || pending;

      if (!shouldRestore) return;
      if (getNotifPermission() !== "granted") return;

      autoRegRef.current = true;

      try {
        const result = await restorePushSubscriptionIfAllowed({
          allowPreferenceEnable: pending,
        });

        if (!alive) return;

        if (result.ok) {
          setNotifPending(uid, false);
          setNotifPermission("granted");
          setNotifPrefEnabled(true);
          setDeviceSubscribed(true);
          setNotifMsg("Notifications active ✅ This device is registered.");
          return;
        }

        if (result.status === NOTIFICATION_STATUS.SUBSCRIPTION_FAILED) {
          setNotifPending(uid, true);
          setNotifPrefEnabled(false);
          setDeviceSubscribed(false);
          setNotifMsg(result.message);
        }
      } catch (notificationError) {
        if (!alive) return;

        setNotifMsg(
          notificationError?.message ||
            "Could not automatically register this device."
        );
      }
    };

    restoreNotifications();

    return () => {
      alive = false;
    };
  }, [uid, notifPrefEnabled]);

  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        setLoading(true);
        setError("");

        const { data: userData, error: authErr } =
          await supabase.auth.getUser();

        if (authErr) throw authErr;

        const user = userData?.user || null;

        if (!user) {
          navigate("/login", { replace: true });
          return;
        }

        setAuthUser(user);
        setUid(user.id);

        const profileRow = await getOrCreateUserProfile();

        // Read the persisted phone directly rather than relying solely on the
        // profile helper's returned shape. Supabase Auth may also store a phone
        // on user.phone (not necessarily in user_metadata).
        const { data: persistedUser, error: persistedError } = await supabase
          .from("user")
          .select("id, full_name, mobile_number, email")
          .eq("id", user.id)
          .maybeSingle();
        if (persistedError) throw persistedError;
        if (!alive) return;

        const md = user.user_metadata || {};
        const resolvedUser = { ...(profileRow || {}), ...(persistedUser || {}) };
        const resolvedPhone = [
          persistedUser?.mobile_number,
          profileRow?.mobile_number,
          user.phone,
          md.mobile_number,
          md.phone,
          md.phone_number,
        ].find((value) => typeof value === "string" && value.trim()) || "";
        resolvedUser.mobile_number = resolvedPhone;
        setUserInfo(resolvedUser);

        setUserForm({
          full_name: resolvedUser.full_name || md.full_name || "",
          mobile_number: resolvedPhone,
          email: user.email || "",
        });

        const [{ data: bizRow, error: bizErr }, { data: learnerRow, error: learnerErr }] = await Promise.all([
          supabase.from("business_profiles")
            .select("id,user_id,business_name,business_setup_type,trading_duration_range,employee_count_range,industry,province,annual_revenue_range,finance_tracking_method,business_banking_type,legal_nature,bank_accounts_separate")
            .eq("user_id", user.id).maybeSingle(),
          supabase.from("user_onboarding_profile")
            .select("business_priorities,learning_days,reminder_times,onboarding_completed_at")
            .eq("user_id", user.id).maybeSingle(),
        ]);
        if (!alive) return;
        if (bizErr) throw bizErr;
        if (learnerErr) throw learnerErr;
        setBusinessInfo(bizRow || null);
        setLearnerInfo(learnerRow || null);
        setBizForm({
          business_name: bizRow?.business_name || "",
          business_setup_type: bizRow?.business_setup_type || "",
          trading_duration_range: bizRow?.trading_duration_range || "",
          employee_count_range: bizRow?.employee_count_range || "",
          industry: bizRow?.industry || "",
          province: profileRow?.province || bizRow?.province || "",
          annual_revenue_range: bizRow?.annual_revenue_range || "",
          finance_tracking_method: bizRow?.finance_tracking_method || "",
          business_banking_type: bizRow?.business_banking_type || "",
        });
        setLearnerForm({
          business_priorities: Array.isArray(learnerRow?.business_priorities) ? learnerRow.business_priorities : [],
          learning_days: Array.isArray(learnerRow?.learning_days) ? learnerRow.learning_days : [],
          reminder_times: Array.isArray(learnerRow?.reminder_times) ? learnerRow.reminder_times : [],
        });

        const notificationSnapshot = await getNotificationSnapshot();

        if (!alive) return;

        setNotifPermission(notificationSnapshot.permission);
        setNotifPrefEnabled(
          notificationSnapshot.storedStatus ===
            NOTIFICATION_STATUS.NOT_REQUESTED
            ? null
            : notificationSnapshot.notificationsEnabled
        );
        setDeviceSubscribed(notificationSnapshot.deviceSubscribed);
      } catch (e) {
        console.error("Profile load error:", e);

        if (alive) {
          setError(e.message || "Failed to load profile.");
        }
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [navigate]);

  const view = useMemo(() => {
    if (!userInfo) return null;

    const md = authUser?.user_metadata || {};

    return {
      full_name: userInfo.full_name || md.full_name || "(no name)",
      email: authUser?.email || "",
      mobile_number:
        userInfo.mobile_number || authUser?.phone || md.mobile_number || md.phone || "",
    };
  }, [userInfo, authUser]);

  const phoneMissing = !view?.mobile_number?.trim();
  const autoGateRef = useRef(false);

  async function saveUser() {
    setUserSaved(false);
    setError("");
    setSavingUser(true);

    try {
      const { data: udata, error: aerr } = await supabase.auth.getUser();

      if (aerr) throw aerr;

      const user = udata?.user;

      if (!user) throw new Error("Not authenticated.");

      const md = authUser?.user_metadata || {};

      const beforeValues = pickAuditValues(
        {
          full_name: userInfo?.full_name || md.full_name || "",
          mobile_number:
            userInfo?.mobile_number ||
            authUser?.phone ||
            md.mobile_number ||
            md.phone ||
            "",
          email: userInfo?.email || authUser?.email || "",
        },
        USER_AUDIT_FIELDS
      );

      const patch = {
        full_name: (userForm.full_name || md.full_name || "").trim(),
        mobile_number: (
          userForm.mobile_number ||
          user.phone ||
          md.mobile_number ||
          md.phone ||
          ""
        ).trim(),
        email: user.email || "",
      };

      if (!patch.full_name) {
        throw new Error("Full Name is required to continue.");
      }

      if (!patch.mobile_number) {
        throw new Error("Phone number is required to continue.");
      }

      const afterValues = pickAuditValues(patch, USER_AUDIT_FIELDS);
      const changedFields = changedFieldsBetween(
        beforeValues,
        afterValues,
        USER_AUDIT_FIELDS
      );

      if ((userForm.email || "").trim().toLowerCase() !== (user.email || "").trim().toLowerCase()) {
        throw new Error("Email changes require Supabase Auth verification and are not supported in this editor yet.");
      }
      const { data, error } = await supabase
        .from("user")
        .update({ full_name: patch.full_name, mobile_number: patch.mobile_number })
        .eq("id", user.id)
        .select("id, full_name, email, mobile_number, created_at")
        .single();

      if (error) throw error;

      setUserInfo(data);

      if (changedFields.length > 0) {
        await auditProfileAction({
          eventType: "profile_details_updated",
          entityType: "user_profile",
          entityTable: "public.user",
          entityId: user.id,
          changedFields,
          beforeValues,
          afterValues,
          metadata: {
            source: "profile_edit_personal_details",
          },
        });
      }

      setUserSaved(true);
    } catch (e) {
      console.error("saveUser error:", e);
      setError(e.message || "Could not save profile.");
    } finally {
      setSavingUser(false);
    }
  }

  async function saveBusiness() {
    setBizSaved(false);
    setError("");
    setSavingBiz(true);
    try {
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError) throw authError;
      if (!user) throw new Error("Not authenticated.");
      const values = Object.fromEntries(BUSINESS_FIELDS.map((key) => [key, String(bizForm[key] || "").trim()]));
      const missing = BUSINESS_FIELDS.filter((key) => !values[key]);
      if (missing.length) throw new Error("Please complete all business questions before saving.");
      const beforeValues = pickAuditValues(businessInfo || {}, BUSINESS_AUDIT_FIELDS);
      const banking = values.business_banking_type;
      const mappedLegalNature = LEGAL_NATURE_MAP[values.business_setup_type];
      const payload = { ...values };
      if (mappedLegalNature) payload.legal_nature = mappedLegalNature;
      if (banking === BUSINESS_BANKING_OPTIONS[0]) payload.bank_accounts_separate = true;
      else if (banking === BUSINESS_BANKING_OPTIONS[1]) payload.bank_accounts_separate = false;
      else if (banking === BUSINESS_BANKING_OPTIONS[2]) payload.bank_accounts_separate = null;
      // Only fields owned by this settings screen are written. Legacy attributes are preserved.
      const { data, error: saveError } = await supabase.from("business_profiles")
        .upsert({ user_id: user.id, ...payload }, { onConflict: "user_id" })
        .select("id,user_id,business_name,business_setup_type,trading_duration_range,employee_count_range,industry,province,annual_revenue_range,finance_tracking_method,business_banking_type,legal_nature,bank_accounts_separate")
        .single();
      if (saveError) throw saveError;
      setBusinessInfo(data);
      const afterValues = pickAuditValues(data, BUSINESS_AUDIT_FIELDS);
      const changedFields = changedFieldsBetween(beforeValues, afterValues, BUSINESS_AUDIT_FIELDS);
      if (changedFields.length) await auditProfileAction({
        eventType: businessInfo ? "business_profile_updated" : "business_profile_created",
        entityType: "business_profile", entityTable: "public.business_profiles",
        entityId: data?.id || user.id, changedFields, beforeValues, afterValues,
        metadata: { source: "profile_edit_business_details_v3" },
      });
      // Province is shared with public.user in the current onboarding.
      const { error: provinceError } = await supabase.from("user")
        .update({ province: values.province }).eq("id", user.id);
      if (provinceError) throw provinceError;
      setBizSaved(true);
      signalBusinessProfileUpdated(true);
    } catch (e) {
      console.error("saveBusiness error:", e);
      setError(e.message || "Could not save business profile.");
    } finally {
      setSavingBiz(false);
    }
  }

  async function saveLearnerPreferences() {
    setLearnerSaved(false);
    setError("");
    setSavingLearner(true);
    try {
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError) throw authError;
      if (!user) throw new Error("Not authenticated.");
      if (learnerForm.business_priorities.length !== 1) throw new Error("Choose one main Gwin goal.");
      if (learnerForm.learning_days.length !== 3) throw new Error("Select exactly three learning days.");
      if (learnerForm.reminder_times.length !== 2) throw new Error("Select exactly two reminder times.");
      const values = Object.fromEntries(LEARNER_FIELDS.map(key => [key, learnerForm[key]]));
      const { error: saveError } = await supabase.from("user_onboarding_profile")
        .upsert({ user_id: user.id, ...values }, { onConflict: "user_id" });
      if (saveError) throw saveError;
      setLearnerInfo(previous => ({ ...(previous || {}), ...values }));
      setLearnerSaved(true);
    } catch (e) {
      setError(e?.message || "Could not save learning preferences.");
    } finally {
      setSavingLearner(false);
    }
  }

  const handleBeforeAccountDelete = useCallback(async () => {
    if (!uid) return;

    try {
      await disablePushNotifications({
        allDevices: true,
      });
    } catch (cleanupError) {
      console.warn(
        "Could not remove notification subscriptions before account deletion:",
        cleanupError
      );
    }
  }, [uid]);

  const handleAccountDeleted = useCallback(async () => {
    try {
      await supabase.auth.signOut({
        scope: "local",
      });
    } catch (signOutError) {
      console.warn(
        "Local sign-out after account deletion failed:",
        signOutError
      );
    }

    try {
      localStorage.clear();
    } catch {}

    try {
      sessionStorage.clear();
    } catch {}

    navigate("/", {
      replace: true,
    });
  }, [navigate]);

  if (loading || !view) {
    return (
      <div className="mt-32 text-center font-sans text-sm text-[#757575]">
        Loading profile…
      </div>
    );
  }
  const profileMenuItems = [
  {
    key: "personal",
    title: "Personal details",
    description: "Name, email and mobile number",
    icon: User,
  },
  {
    key: "business",
    title: "Business profile",
    description: "Business details, industry, revenue and province",
    icon: Building2,
  },
  {
    key: "learning",
    title: "Learning preferences",
    description: "Your goals, learning days and reminder times",
    icon: Users,
  },
  {
    key: "calendar",
    title: "Financial calendar",
    description: "Manage your tax and financial reminders",
    icon: Building2,
  },
  {
    key: "security",
    title: "Account security",
    description: "Password and account access",
    icon: KeyRound,
  },
  {
    key: "notifications",
    title: "Notifications",
    description: "Notification status and device settings",
    icon: Bell,
  },
  {
  key: "privacy",
  title: "Privacy & permissions",
  description: "Consent, data sharing and privacy choices",
  icon: Bell,
},
  {
    key: "referrals",
    title: "Invite & unlock paid levels",
    description: "Referral code, invite link and unlock progress",
    icon: Gift,
  },
  {
    key: "support",
    title: "Help & support",
    description: "Support team, contact and bug reports",
    icon: MessageCircle,
  },
  {
    key: "danger",
    title: "Delete account",
    description: "Permanently remove your account",
    icon: Trash2,
    danger: true,
  },
];

function openProfilePanel(panelKey) {
  setActiveProfilePanel(panelKey);
}

function backToProfileMenu() {
  setActiveProfilePanel("menu");
}

  

return (
  <>
    <AppScreenShell
      showHeader={false}
      showPenguinBackground={false}
      showBottomBar={false}
      maxWidth="max-w-lg"
      contentClassName="!px-0 !pt-0"
    >
      <Banner homeTo="/dashboard" />

      <section className="relative overflow-hidden bg-white px-6 pb-5 pt-6">
        <div className="relative min-h-[145px]">
          <div className="relative z-10 max-w-[230px] pt-5">
            <h1 className="text-[27px] font-black leading-tight tracking-[-0.035em] text-[#111111]">
              Profile & Settings
            </h1>

            <p className="mt-2 text-[11px] font-medium leading-relaxed text-[#858585]">
              Manage your details, business info, privacy and account settings.
            </p>
          </div>

          <img
            src={PROFILE_SUNGLASSES_SRC}
            alt=""
            aria-hidden="true"
            className="pointer-events-none absolute -bottom-1 right-0 h-[135px] w-[145px] object-contain"
            draggable="false"
          />
        </div>
      </section>

      <div className="relative min-h-[calc(100vh-220px)] overflow-hidden bg-[#F8F8F8]">
        <img
          src={PROFILE_BOTTOM_PENGUIN_SRC}
          alt=""
          aria-hidden="true"
          className="pointer-events-none absolute bottom-[-22px] right-[-88px] z-0 w-[245px] max-w-none -rotate-[37deg]"
          draggable="false"
        />

        <div className="relative z-10 px-5 pb-28 pt-5">
        {error && (
          <div className="mb-5 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        )}

        {activeProfilePanel === "menu" && (
  <>
    <div className="space-y-3">
      {profileMenuItems.map((item) => {
        const Icon = item.icon;

        return (
          <button
            key={item.key}
            type="button"
            onClick={() => openProfilePanel(item.key)}
            className={[
              "flex w-full items-center gap-3 rounded-[26px] border bg-white/95 p-4 text-left shadow-sm transition active:scale-[0.99]",
              item.danger
                ? "border-red-200 hover:bg-red-50"
                : "border-[#EFEFEF] hover:border-[#FF914D]/40 hover:bg-[#FFF9F5]",
            ].join(" ")}
          >
            <div
              className={[
                "flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl",
                item.danger
                  ? "bg-red-100 text-red-700"
                  : "bg-[#FFF4EC] text-[#FF914D]",
              ].join(" ")}
            >
              <Icon className="h-5 w-5" />
            </div>

            <div className="min-w-0 flex-1">
              <p
                className={[
                  "text-sm font-black leading-tight",
                  item.danger ? "text-red-900" : "text-[#333333]",
                ].join(" ")}
              >
                {item.title}
              </p>

              <p
                className={[
                  "mt-1 text-xs font-semibold leading-relaxed",
                  item.danger ? "text-red-700" : "text-[#757575]",
                ].join(" ")}
              >
                {item.description}
              </p>
            </div>

            <ChevronRight
              className={[
                "h-5 w-5 shrink-0",
                item.danger ? "text-red-400" : "text-[#BCBCBC]",
              ].join(" ")}
            />
          </button>
        );
      })}
    </div>

    <button
      type="button"
      onClick={async () => {
        await supabase.auth.signOut();

        navigate("/login", {
          replace: true,
        });
      }}
      className="mt-6 flex w-full items-center justify-center rounded-2xl border border-[#E5E5E5] bg-white px-5 py-3 text-sm font-bold text-[#333333] shadow-sm hover:bg-[#FAFAFA]"
    >
      <LogOut className="mr-2 h-4 w-4" />
      Log out
    </button>
  </>
)}

{activeProfilePanel !== "menu" && (
  <button
    type="button"
    onClick={backToProfileMenu}
    className="mb-4 inline-flex items-center gap-2 rounded-full border border-[#EFEFEF] bg-white px-4 py-2 text-xs font-black text-[#757575] shadow-sm"
  >
    <ArrowLeft className="h-4 w-4" />
    Back to settings
  </button>
)}

{activeProfilePanel === "personal" && (
  <Section icon={User} title="Personal details">
    <Field
      label="Full Name *"
      value={userForm.full_name}
      onChange={(value) =>
        setUserForm((form) => ({
          ...form,
          full_name: value,
        }))
      }
    />

    <Field
      label="Phone Number *"
      value={userForm.mobile_number}
      onChange={(value) =>
        setUserForm((form) => ({
          ...form,
          mobile_number: value,
        }))
      }
    />

    {!userForm.mobile_number?.trim() && (
      <p className="mb-4 text-xs text-amber-700">
        No phone number was found in your saved profile or Auth account.
        Enter your number and select Save profile to store it.
      </p>
    )}

    <div className="mb-5">
      <p className="text-[13px] font-bold uppercase text-[#757575]">Sign-in email</p>
      <p className="mt-2 rounded-2xl border bg-[#FAFAFA] px-4 py-3 text-sm">{authUser?.email || ""}</p>
      <p className="mt-1 text-xs text-[#757575]">Email changes require a verified Supabase Auth flow.</p>
    </div>

    <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-center">
      <button
        type="button"
        onClick={saveUser}
        disabled={savingUser}
        className="inline-flex items-center justify-center rounded-full bg-[#FF914D] px-5 py-2.5 text-sm font-bold text-white hover:bg-[#E9782F] disabled:opacity-60"
      >
        {savingUser ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Saving…
          </>
        ) : (
          <>
            <Save className="mr-2 h-4 w-4" />
            Save profile
          </>
        )}
      </button>

      {userSaved && (
        <span className="text-sm font-semibold text-green-700">Saved!</span>
      )}
    </div>
  </Section>
)}

{activeProfilePanel === "business" && (
  <Section icon={Building2} title="Business profile">
    <p className="mb-4 text-xs text-[#757575]">These are the same questions used during your current onboarding. Older business data is preserved.</p>
    <Field label="Business name" value={bizForm.business_name} onChange={value => setBizForm(form => ({ ...form, business_name: value }))} />
    {[
      ["business_setup_type", "How is your business set up?", BUSINESS_SETUP_OPTIONS],
      ["trading_duration_range", "How long have you been trading?", TRADING_DURATION_OPTIONS],
      ["employee_count_range", "How many people work in the business, including you?", EMPLOYEE_COUNT_OPTIONS_V3],
      ["industry", "Industry", INDUSTRY_OPTIONS_V3],
      ["province", "Province", PROVINCE_OPTIONS_V3],
      ["annual_revenue_range", "Annual revenue", ANNUAL_REVENUE_OPTIONS_V3],
      ["finance_tracking_method", "How do you track your finances?", FINANCE_TRACKING_OPTIONS_V3],
      ["business_banking_type", "Business banking", BUSINESS_BANKING_OPTIONS],
    ].map(([key,label,options]) => (
      <SelectField key={key} label={label} value={bizForm[key]} options={options}
        onChange={value => setBizForm(form => ({ ...form, [key]: value }))} />
    ))}
    <button type="button" onClick={saveBusiness} disabled={savingBiz}
      className="inline-flex items-center justify-center rounded-full bg-[#FF914D] px-5 py-2.5 text-sm font-bold text-white disabled:opacity-60">
      {savingBiz ? "Saving…" : "Save business details"}
    </button>
    {bizSaved && <span className="ml-3 text-sm font-semibold text-green-700">Saved!</span>}
  </Section>
)}

{activeProfilePanel === "learning" && (
  <Section icon={Users} title="Learning preferences">
    <SelectField label="What would you like Gwin to help you with first?"
      value={learnerForm.business_priorities[0] || ""} options={GWIN_FOCUS_OPTIONS}
      onChange={value => setLearnerForm(form => ({...form, business_priorities: value ? [value] : []}))} />
    {[
      ["learning_days", "Choose exactly 3 learning days", LEARNING_DAY_OPTIONS_V3, 3],
      ["reminder_times", "Choose exactly 2 reminder times", CHECK_IN_TIME_OPTIONS, 2],
    ].map(([key,label,options,max]) => (
      <div className="mb-5" key={key}>
        <p className="mb-2 text-sm font-bold text-[#333333]">{label}</p>
        <div className="flex flex-wrap gap-2">{options.map(option => {
          const selected = learnerForm[key].includes(option);
          return <button key={option} type="button" aria-pressed={selected}
            className={selected ? "rounded-full bg-[#FF914D] px-3 py-2 text-xs font-bold text-white" : "rounded-full border bg-white px-3 py-2 text-xs font-bold text-[#555555]"}
            onClick={() => setLearnerForm(form => ({...form,
              [key]: selected ? form[key].filter(v => v !== option) : form[key].length < max ? [...form[key],option] : form[key],
            }))}>{option}</button>;
        })}</div>
        <p className="mt-2 text-xs text-[#757575]">{learnerForm[key].length} of {max} selected</p>
      </div>
    ))}
    <button type="button" onClick={saveLearnerPreferences} disabled={savingLearner}
      className="rounded-full bg-[#FF914D] px-5 py-2.5 text-sm font-bold text-white disabled:opacity-60">
      {savingLearner ? "Saving…" : "Save learning preferences"}
    </button>
    {learnerSaved && <span className="ml-3 text-sm font-semibold text-green-700">Saved!</span>}
  </Section>
)}

{activeProfilePanel === "calendar" && (
  <Section icon={Building2} title="Financial calendar">
    <p className="mb-4 text-sm text-[#757575]">Manage your financial-calendar questionnaire and its applicable reminders in the existing calendar setup flow.</p>
    <button type="button" onClick={() => navigate("/calendar/setup")}
      className="rounded-full bg-[#FF914D] px-5 py-2.5 text-sm font-bold text-white">Open financial calendar setup</button>
  </Section>
)}

{activeProfilePanel === "security" && (
  <Section icon={KeyRound} title="Account security">
    <p className="text-sm leading-relaxed text-[#757575]">
      Create a new password for your account. Use the same password rules as
      registration.
    </p>

    {passwordError && (
      <div className="mt-3 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
        {passwordError}
      </div>
    )}

    {passwordMessage && (
      <div className="mt-3 rounded-2xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">
        {passwordMessage}
      </div>
    )}

    <PasswordField
      label="New Password"
      value={passwordForm.newPassword}
      onChange={(value) =>
        setPasswordForm((form) => ({
          ...form,
          newPassword: value,
        }))
      }
      show={showNewPassword}
      onToggle={() => setShowNewPassword((value) => !value)}
    />

    <PasswordCriteria password={passwordForm.newPassword} />

    <PasswordField
      label="Confirm New Password"
      value={passwordForm.confirmPassword}
      onChange={(value) =>
        setPasswordForm((form) => ({
          ...form,
          confirmPassword: value,
        }))
      }
      show={showConfirmPassword}
      onToggle={() => setShowConfirmPassword((value) => !value)}
    />

    <button
      type="button"
      onClick={changePassword}
      disabled={
        changingPassword ||
        !passwordForm.newPassword ||
        !passwordForm.confirmPassword
      }
      className="inline-flex items-center justify-center rounded-full bg-[#FF914D] px-5 py-2.5 text-sm font-bold text-white hover:bg-[#E9782F] disabled:opacity-60"
    >
      {changingPassword ? (
        <>
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          Updating…
        </>
      ) : (
        <>
          <Save className="mr-2 h-4 w-4" />
          Update password
        </>
      )}
    </button>
  </Section>
)}

{activeProfilePanel === "notifications" && (
  <Section icon={Bell} title="Notifications">
    <div className="rounded-2xl border border-dashed border-[#E5E5E5] bg-[#FAFAFA] p-4">
      <p className="text-sm font-semibold text-[#333333]">Notifications</p>

      <p className="mt-1 text-xs leading-relaxed text-[#757575]">
        Current:{" "}
        <span className="font-semibold text-[#333333]">
          {notifLabel(notifPermission, notifPrefEnabled)}
        </span>{" "}
        • Browser: {notifPermission === "default" ? "ask" : notifPermission} •
        Device: {deviceSubscribed ? "registered" : "not registered"}
      </p>

      {needsInstallNote && (
        <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 p-3">
          <p className="text-xs leading-relaxed text-amber-800">
            On iPhone or iPad, Gwin must be added to your Home Screen before
            notifications can be enabled.
          </p>

          <button
            type="button"
            onClick={() =>
              navigate("/app", {
                state: {
                  fromNotificationSetup: true,
                  returnTo: window.location.pathname,
                },
              })
            }
            className="mt-2 text-xs font-bold text-amber-900 underline"
          >
            View installation instructions
          </button>
        </div>
      )}

      {notifPermission === "denied" && (
        <p className="mt-3 text-xs leading-relaxed text-amber-800">
          {blockedStepsText()}
        </p>
      )}

      {notifMsg && (
        <p className="mt-3 text-xs leading-relaxed text-[#757575]">
          {notifMsg}
        </p>
      )}

      <div className="mt-4 flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => setNotificationsEnabled(true)}
          disabled={notifBusy || !uid}
          className="rounded-full bg-[#FF914D] px-4 py-2 text-xs font-bold text-white hover:bg-[#E9782F] disabled:opacity-60"
        >
          {notifBusy ? "Working…" : "Enable"}
        </button>

        <button
          type="button"
          onClick={() => setNotificationsEnabled(false)}
          disabled={notifBusy || !uid}
          className="rounded-full bg-[#F3F3F3] px-4 py-2 text-xs font-bold text-[#333333] hover:bg-[#E8E8E8] disabled:opacity-60"
        >
          {notifBusy ? "Working…" : "Disable"}
        </button>

        <button
          type="button"
          onClick={refreshNotifStatus}
          className="rounded-full border border-[#E5E5E5] px-4 py-2 text-xs font-bold text-[#757575] hover:bg-[#FAFAFA]"
        >
          Refresh
        </button>
      </div>
    </div>
  </Section>
)}

{activeProfilePanel === "privacy" && (
  <Section icon={Bell} title="Privacy & permissions">
    <div className="space-y-5">
      <PrivacyPermissionsSection />
      <PartnerFinancialSharingSection />
    </div>
  </Section>
)}

{activeProfilePanel === "referrals" && (
  <Section icon={Gift} title="Invite & unlock paid levels">
    <ReferralInviteCard
      summary={referralSummary}
      loading={referralLoading}
      error={referralError}
      inviteLink={referralInviteLink}
      copied={referralCopied}
      message={referralShareMessage}
      onCopy={copyReferralLink}
      onShare={shareReferralLink}
      onRefresh={loadReferralSummary}
    />
  </Section>
)}

{activeProfilePanel === "support" && (
  <>
    {!supportLoading && supportContacts.length > 0 && (
      <Section icon={MessageCircle} title="Your support team">
        <div className="space-y-3">
          {supportContacts.map((contact) => (
            <SupportContactCard
              key={`${contact.cohort_learner_id}-${contact.partner_member_id}`}
              contact={contact}
            />
          ))}
        </div>
      </Section>
    )}

    <GwinBugReportPanel source="learner_profile" />

    <button
      type="button"
      onClick={() => navigate("/dashboard/contact")}
      className="mt-4 inline-flex w-full items-center justify-center rounded-2xl border border-[#FF914D] bg-white px-5 py-3 text-sm font-bold text-[#FF914D] hover:bg-[#FFF4EC]"
    >
      Contact & Legal Notices
    </button>
  </>
)}

{activeProfilePanel === "danger" && (
  <section className="rounded-3xl border border-red-200 bg-red-50 p-5 shadow-sm">
    <div className="flex items-start gap-3">
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-red-100 text-red-700">
        <Trash2 className="h-5 w-5" />
      </div>

      <div className="min-w-0 flex-1">
        <p className="text-sm font-bold text-red-900">Delete account</p>

        <p className="mt-1 text-xs leading-relaxed text-red-800">
          Permanently remove your Gwin account, profile information, learning
          progress, XP, practical challenge data, achievements, referral
          progress, and app access.
        </p>
      </div>
    </div>

    <button
      type="button"
      onClick={() => setDeleteAccountOpen(true)}
      className="mt-4 inline-flex w-full items-center justify-center rounded-2xl border border-red-300 bg-white px-5 py-3 text-sm font-bold text-red-700 transition hover:bg-red-100"
    >
      <Trash2 className="mr-2 h-4 w-4" />
      Delete my account
    </button>
  </section>
)}
              </div>
      </div>
    </AppScreenShell>

      <DeleteAccountFlow
              open={deleteAccountOpen}
        userEmail={view?.email}
        onClose={() => setDeleteAccountOpen(false)}
        onBeforeDelete={handleBeforeAccountDelete}
        onDeleted={handleAccountDeleted}
      />

      
    </>
  );
}

const GateNotice = ({
  phoneMissing,
  bizIncomplete,
  notifMissing,
}) => {
  return (
    <div
      role="alert"
      className="mt-4 rounded-2xl border-2 border-amber-400 bg-amber-50 px-4 py-4 shadow-sm md:px-5 md:py-5"
    >
      <div className="flex items-start gap-3">
        <span
          className="text-2xl"
          aria-hidden="true"
        >
          ⚠️
        </span>

        <div className="min-w-0 space-y-1">
          <p className="whitespace-normal break-words text-sm font-semibold text-amber-900 md:text-base">
            Complete your profile to unlock the app
          </p>

          <p className="whitespace-normal break-words text-xs leading-snug text-amber-900/90 md:text-sm md:leading-relaxed">
            You&apos;ll only be able to access{" "}
            <strong>Dashboard</strong> and{" "}
            <strong>Achievements</strong> once you&apos;ve added your{" "}
            {phoneMissing && bizIncomplete && notifMissing && (
              <span>
                <strong>phone number</strong>,{" "}
                <strong>business details</strong>, and{" "}
                <strong>notification preference</strong>.
              </span>
            )}

            {phoneMissing && bizIncomplete && !notifMissing && (
              <span>
                <strong>phone number</strong> and{" "}
                <strong>business details</strong>.
              </span>
            )}

            {phoneMissing && !bizIncomplete && notifMissing && (
              <span>
                <strong>phone number</strong> and{" "}
                <strong>notification preference</strong>.
              </span>
            )}

            {phoneMissing && !bizIncomplete && !notifMissing && (
              <span>
                <strong>phone number</strong>.
              </span>
            )}

            {!phoneMissing && bizIncomplete && notifMissing && (
              <span>
                <strong>business details</strong> and{" "}
                <strong>notification preference</strong>.
              </span>
            )}

            {!phoneMissing && bizIncomplete && !notifMissing && (
              <span>
                <strong>business details</strong>.
              </span>
            )}

            {!phoneMissing && !bizIncomplete && notifMissing && (
              <span>
                <strong>notification preference</strong>.
              </span>
            )}
          </p>
        </div>
      </div>
    </div>
  );
};

function SupportContactCard({
  contact,
}) {
  const [
    requestOpen,
    setRequestOpen,
  ] = useState(false);

  const [
    subject,
    setSubject,
  ] = useState("");

  const [
    message,
    setMessage,
  ] = useState("");

  const [
    sending,
    setSending,
  ] = useState(false);

  const [
    requestError,
    setRequestError,
  ] = useState("");

  const [
    requestSent,
    setRequestSent,
  ] = useState(false);


  const isFacilitator =
    contact.role ===
    "facilitator";

  const roleLabel =
    isFacilitator
      ? "Facilitator"
      : contact.role ===
          "cohort_manager"
        ? "Cohort manager"
        : "Support team member";


  function openRequest() {
    setRequestError("");
    setRequestSent(false);
    setRequestOpen(true);
  }


  function closeRequest() {
    if (sending) return;

    setRequestOpen(false);
    setRequestError("");
  }


  async function submitRequest(
    event
  ) {
    event.preventDefault();

    const cleanSubject =
      String(
        subject || ""
      ).trim();

    const cleanMessage =
      String(
        message || ""
      ).trim();

    setRequestError("");
    setRequestSent(false);

    if (
      cleanSubject.length <
      3
    ) {
      setRequestError(
        "Please enter a subject of at least 3 characters."
      );

      return;
    }

    if (
      cleanSubject.length >
      120
    ) {
      setRequestError(
        "Subject cannot be longer than 120 characters."
      );

      return;
    }

    if (
      cleanMessage.length <
      10
    ) {
      setRequestError(
        "Please tell your support contact a little more about what you need help with."
      );

      return;
    }

    if (
      cleanMessage.length >
      2000
    ) {
      setRequestError(
        "Message cannot be longer than 2000 characters."
      );

      return;
    }

    try {
      setSending(true);

      await sendLearnerSupportRequest(
        {
          cohortLearnerId:
            contact
              .cohort_learner_id,

          partnerMemberId:
            contact
              .partner_member_id,

          subject:
            cleanSubject,

          message:
            cleanMessage,
        }
      );

      setRequestSent(true);

      setSubject("");
      setMessage("");
    } catch (
      sendError
    ) {
      console.error(
        "Learner support request failed:",
        sendError
      );

      setRequestError(
        sendError?.message ||
          "Your support request could not be sent."
      );
    } finally {
      setSending(false);
    }
  }


  return (
    <>
      <div className="rounded-2xl border border-[#FFE0CC] bg-[#FFF7F2] p-4">
        <div className="flex items-start gap-3">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-[#FF914D] text-white">
            <User className="h-5 w-5" />
          </div>

          <div className="min-w-0 flex-1">
            <p className="font-bold text-[#333333]">
              {contact.full_name ||
                roleLabel}
            </p>

            <p className="mt-1 text-xs font-bold uppercase tracking-wider text-[#FF914D]">
              {roleLabel}
            </p>

            <p className="mt-2 text-xs leading-relaxed text-[#757575]">
              {contact.cohort_name}
            </p>

            {isFacilitator && (
              <p className="mt-2 text-xs leading-relaxed text-[#757575]">
                Your assigned facilitator
                can support you with your
                learning journey and cohort
                activities.
              </p>
            )}

            {!isFacilitator &&
              contact.role ===
                "cohort_manager" && (
                <p className="mt-2 text-xs leading-relaxed text-[#757575]">
                  Your cohort manager can
                  support you with cohort
                  questions and learning
                  support.
                </p>
              )}

            <button
              type="button"
              onClick={
                openRequest
              }
              className="mt-4 inline-flex w-full items-center justify-center rounded-full bg-[#FF914D] px-4 py-2.5 text-sm font-black text-white transition hover:bg-[#E9782F]"
            >
              <MessageCircle className="mr-2 h-4 w-4" />

              Request support
            </button>
          </div>
        </div>
      </div>


      {requestOpen && (
        <div
          className="fixed inset-0 z-[150] flex items-center justify-center bg-black/40 px-4 py-6"
          role="dialog"
          aria-modal="true"
          aria-labelledby="learner-support-request-title"
          onMouseDown={(
            event
          ) => {
            if (
              event.target ===
                event.currentTarget &&
              !sending
            ) {
              closeRequest();
            }
          }}
        >
          <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-3xl bg-white shadow-2xl">
            <div className="flex items-start justify-between gap-4 border-b border-[#EFEFEF] px-5 py-5">
              <div>
                <p className="text-xs font-black uppercase tracking-wider text-[#FF914D]">
                  Request support
                </p>

                <h2
                  id="learner-support-request-title"
                  className="mt-1 text-xl font-black text-[#333333]"
                >
                  Contact{" "}
                  {contact.full_name ||
                    roleLabel}
                </h2>

                <p className="mt-1 text-xs text-[#757575]">
                  {roleLabel}
                  {" · "}
                  {contact.cohort_name}
                </p>
              </div>

              <button
                type="button"
                onClick={
                  closeRequest
                }
                disabled={
                  sending
                }
                className="rounded-full p-2 text-[#757575] transition hover:bg-[#F5F5F5] disabled:opacity-50"
                aria-label="Close support request"
              >
                <X className="h-5 w-5" />
              </button>
            </div>


            {requestSent ? (
              <div className="px-5 py-8 text-center">
                <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-green-100 text-green-700">
                  <CheckCircle2 className="h-7 w-7" />
                </div>

                <h3 className="mt-4 text-lg font-black text-[#333333]">
                  Request sent
                </h3>

                <p className="mt-2 text-sm leading-relaxed text-[#757575]">
                  Your support request has
                  been sent to{" "}
                  {contact.full_name ||
                    roleLabel}.
                </p>

                <p className="mt-1 text-xs leading-relaxed text-[#757575]">
                  They will see it in their
                  G-Win support inbox.
                </p>

                <button
                  type="button"
                  onClick={
                    closeRequest
                  }
                  className="mt-6 inline-flex w-full items-center justify-center rounded-full bg-[#FF914D] px-5 py-3 text-sm font-black text-white transition hover:bg-[#E9782F]"
                >
                  Done
                </button>
              </div>
            ) : (
              <form
                onSubmit={
                  submitRequest
                }
                className="space-y-5 px-5 py-5"
              >
                {requestError && (
                  <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                    {requestError}
                  </div>
                )}

                <div>
                  <label
                    htmlFor={`support-subject-${contact.partner_member_id}`}
                    className="text-sm font-bold text-[#333333]"
                  >
                    What do you need help with?
                  </label>

                  <input
                    id={`support-subject-${contact.partner_member_id}`}
                    type="text"
                    value={
                      subject
                    }
                    onChange={(
                      event
                    ) =>
                      setSubject(
                        event
                          .target
                          .value
                      )
                    }
                    maxLength={
                      120
                    }
                    disabled={
                      sending
                    }
                    placeholder="e.g. I need help with my current lesson"
                    className="mt-2 w-full rounded-2xl border border-[#E5E5E5] bg-white px-4 py-3 text-sm text-[#333333] outline-none transition focus:border-[#FF914D] focus:ring-4 focus:ring-[#FF914D]/15 disabled:opacity-60"
                  />

                  <p className="mt-1 text-right text-xs text-[#999999]">
                    {
                      subject.length
                    }
                    /120
                  </p>
                </div>


                <div>
                  <label
                    htmlFor={`support-message-${contact.partner_member_id}`}
                    className="text-sm font-bold text-[#333333]"
                  >
                    Message
                  </label>

                  <textarea
                    id={`support-message-${contact.partner_member_id}`}
                    value={
                      message
                    }
                    onChange={(
                      event
                    ) =>
                      setMessage(
                        event
                          .target
                          .value
                      )
                    }
                    maxLength={
                      2000
                    }
                    rows={
                      6
                    }
                    disabled={
                      sending
                    }
                    placeholder="Tell your support contact what is happening and what you need help with."
                    className="mt-2 w-full resize-none rounded-2xl border border-[#E5E5E5] bg-white px-4 py-3 text-sm leading-relaxed text-[#333333] outline-none transition focus:border-[#FF914D] focus:ring-4 focus:ring-[#FF914D]/15 disabled:opacity-60"
                  />

                  <p className="mt-1 text-right text-xs text-[#999999]">
                    {
                      message.length
                    }
                    /2000
                  </p>
                </div>


                <div className="rounded-2xl bg-[#FFF7F2] px-4 py-3 text-xs leading-relaxed text-[#75523B]">
                  This message will only be
                  sent to the support contact
                  assigned to you for this
                  cohort.
                </div>


                <button
                  type="submit"
                  disabled={
                    sending
                  }
                  className="inline-flex w-full items-center justify-center rounded-full bg-[#FF914D] px-5 py-3 text-sm font-black text-white transition hover:bg-[#E9782F] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {sending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Sending…
                    </>
                  ) : (
                    <>
                      <Send className="mr-2 h-4 w-4" />
                      Send support request
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
}

function ReferralInviteCard({
  summary,
  loading,
  error,
  inviteLink,
  copied,
  message,
  onCopy,
  onShare,
  onRefresh,
}) {
  const qualifiedCount = Number(summary?.qualified_count || 0);
  const pendingCount = Number(summary?.pending_count || 0);
  const requiredCount = Number(summary?.required_count || 5);

  const remainingCount = Math.max(
    0,
    Number(
      summary?.remaining_count ??
        requiredCount - qualifiedCount
    )
  );

  const hasReward = !!summary?.has_reward;

  const progressPercent =
    requiredCount > 0
      ? Math.max(
          0,
          Math.min(
            100,
            Math.round(
              (qualifiedCount / requiredCount) * 100
            )
          )
        )
      : 0;

  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-[#FFE0CC] bg-[#FFF7F2] p-4">
        <div className="flex items-start gap-3">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-[#FF914D] text-white">
            <Users className="h-5 w-5" />
          </div>

          <div className="min-w-0 flex-1">
            <p className="text-sm font-bold text-[#333333]">
              Invite 5 entrepreneurs
            </p>

            <p className="mt-1 text-xs leading-relaxed text-[#757575]">
              When 5 people register through your link and use the
              app, you unlock access to the once-off paid 50 levels
              for free.
            </p>
          </div>
        </div>

        <div className="mt-4">
          <div className="flex items-center justify-between text-xs font-bold text-[#333333]">
            <span>
              {qualifiedCount}/{requiredCount} qualified
            </span>

            {hasReward ? (
              <span className="text-green-700">
                Unlocked
              </span>
            ) : (
              <span>{remainingCount} left</span>
            )}
          </div>

          <div className="mt-2 h-3 overflow-hidden rounded-full bg-white">
            <div
              className="h-full rounded-full bg-[#FF914D] transition-all duration-500"
              style={{
                width: `${progressPercent}%`,
              }}
            />
          </div>
        </div>

        <div className="mt-4 grid grid-cols-2 gap-2">
          <div className="rounded-2xl bg-white px-3 py-3 text-center">
            <p className="text-[10px] font-bold uppercase tracking-wider text-[#757575]">
              Registered
            </p>

            <p className="mt-1 text-xl font-bold text-[#333333]">
              {pendingCount + qualifiedCount}
            </p>
          </div>

          <div className="rounded-2xl bg-white px-3 py-3 text-center">
            <p className="text-[10px] font-bold uppercase tracking-wider text-[#757575]">
              Used app
            </p>

            <p className="mt-1 text-xl font-bold text-[#333333]">
              {qualifiedCount}
            </p>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center rounded-2xl border border-[#EFEFEF] bg-[#FAFAFA] px-4 py-4 text-sm font-semibold text-[#757575]">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          Loading invite progress…
        </div>
      ) : error ? (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      ) : (
        <>
          <div className="rounded-2xl border border-[#E5E5E5] bg-white p-4">
            <p className="text-[11px] font-bold uppercase tracking-wider text-[#757575]">
              Your invite code
            </p>

            <p className="mt-1 break-all text-lg font-bold tracking-wide text-[#333333]">
              {summary?.code || "—"}
            </p>

            <p className="mt-3 text-[11px] font-bold uppercase tracking-wider text-[#757575]">
              Invite link
            </p>

            <p className="mt-1 break-all rounded-xl bg-[#FAFAFA] px-3 py-2 text-xs font-semibold leading-relaxed text-[#333333]">
              {inviteLink || "Invite link unavailable"}
            </p>
          </div>

          {message && (
            <p
              className={[
                "rounded-2xl px-4 py-3 text-sm font-semibold",
                copied
                  ? "bg-green-50 text-green-700"
                  : "bg-[#FAFAFA] text-[#757575]",
              ].join(" ")}
            >
              {message}
            </p>
          )}

          <div className="grid gap-2 sm:grid-cols-3">
            <button
              type="button"
              onClick={onCopy}
              disabled={!inviteLink}
              className="inline-flex items-center justify-center rounded-2xl bg-[#FF914D] px-4 py-3 text-xs font-bold text-white hover:bg-[#E9782F] disabled:opacity-60"
            >
              <Copy className="mr-2 h-4 w-4" />
                Copy link
              </button>

              <button
                type="button"
                onClick={onShare}
                disabled={!inviteLink}
                className="inline-flex items-center justify-center rounded-2xl border border-[#E5E5E5] bg-white px-4 py-3 text-xs font-bold text-[#333333] hover:bg-[#FAFAFA] disabled:opacity-60"
              >
                <Share2 className="mr-2 h-4 w-4" />
                  Share
              </button>

              <button
                type="button"
                onClick={onRefresh}
                disabled={loading}
                className="inline-flex items-center justify-center rounded-2xl border border-[#E5E5E5] bg-white px-4 py-3 text-xs font-bold text-[#333333] hover:bg-[#FAFAFA] disabled:opacity-60"
              >
                <RefreshCw className="mr-2 h-4 w-4" />
                Refresh
              </button>
            </div>
        </>
      )}
    </div>
  );
}

function Section({
  icon: Icon,
  title,
  children,
}) {
  return (
    <section className="mt-6 rounded-3xl border border-[#EFEFEF] bg-white p-5 shadow-sm">
      <div className="mb-4 flex items-center gap-2">
        <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#FFF4EC] text-[#FF914D]">
          <Icon className="h-4 w-4" />
        </div>

        <h2 className="text-lg font-bold text-[#333333]">
          {title}
        </h2>
      </div>

      <div className="space-y-2.5">
        {children}
      </div>
    </section>
  );
}

function DisplayField({
  label,
  value,
}) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-2xl bg-[#FAFAFA] px-4 py-3">
      <span className="text-[11px] font-bold uppercase tracking-wider text-[#757575]">
        {label}
      </span>

      <span className="min-w-0 truncate text-right text-sm font-semibold text-[#333333]">
        {value || "—"}
      </span>
    </div>
  );
}

const Field = memo(function Field({
  label,
  value,
  onChange,
  type = "text",
}) {
  return (
    <div className="mb-5">
      <label className="mb-2 block whitespace-normal break-words text-[13px] font-bold uppercase tracking-wider text-[#757575]">
        {label}
      </label>

      <input
        type={type}
        value={value ?? ""}
        onChange={(event) =>
          onChange(event.target.value)
        }
        placeholder={label}
        className="w-full rounded-2xl border border-[#E5E5E5] bg-white px-4 py-3 text-sm font-semibold text-[#333333] outline-none placeholder:text-[#BCBCBC] focus:border-[#FF914D] focus:ring-4 focus:ring-[#FF914D]/15"
        autoComplete="off"
        inputMode={
          type === "number"
            ? "numeric"
            : undefined
        }
      />
    </div>
  );
});

const PasswordField = memo(function PasswordField({
  label,
  value,
  onChange,
  show,
  onToggle,
}) {
  return (
    <div className="mb-5">
      <label className="mb-2 block whitespace-normal break-words text-[13px] font-bold uppercase tracking-wider text-[#757575]">
        {label}
      </label>

      <div className="relative">
        <input
          type={show ? "text" : "password"}
          value={value ?? ""}
          onChange={(event) =>
            onChange(event.target.value)
          }
          placeholder={label}
          className="w-full rounded-2xl border border-[#E5E5E5] bg-white px-4 py-3 pr-12 text-sm font-semibold text-[#333333] outline-none placeholder:text-[#BCBCBC] focus:border-[#FF914D] focus:ring-4 focus:ring-[#FF914D]/15"
          autoComplete="new-password"
        />

        <button
          type="button"
          onClick={onToggle}
          className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full p-1 text-[#757575] hover:bg-[#F3F3F3]"
          aria-label={
            show
              ? "Hide password"
              : "Show password"
          }
        >
          {show ? (
            <EyeOff className="h-5 w-5" />
          ) : (
            <Eye className="h-5 w-5" />
          )}
        </button>
      </div>
    </div>
  );
});

const PasswordCriteria = memo(function PasswordCriteria({
  password,
}) {
  return (
    <div
      role="region"
      aria-live="polite"
      className="mb-5 rounded-2xl border border-[#E5E5E5] bg-[#FAFAFA] p-4 text-sm text-[#333333]"
    >
      <p className="mb-2 text-sm font-bold text-[#333333]">
        Your password must include:
      </p>

      <ul className="space-y-1">
        {PASSWORD_RULES.map((rule) => {
          const ok = rule.test(password || "");

          return (
            <li
              key={rule.key}
              className="flex items-center gap-2"
            >
              {ok ? (
                <CheckCircle2 className="h-4 w-4 text-green-600" />
              ) : (
                <XCircle className="h-4 w-4 text-[#BCBCBC]" />
              )}

              <span
                className={
                  ok
                    ? "text-green-700"
                    : "text-[#757575]"
                }
              >
                {rule.label}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
});

const RegNoField = memo(function RegNoField({
  label = "Registration Number",
  value,
  onChange,
}) {
  const digits = (value ?? "")
    .replace(/\D/g, "")
    .slice(0, 12);

  const handleChange = (nextValue) => {
    const cleaned = (nextValue || "")
      .replace(/\D/g, "")
      .slice(0, 12);

    onChange(cleaned);
  };

  return (
    <div className="mb-5">
      <label className="mb-2 block whitespace-normal break-words text-[13px] font-bold uppercase tracking-wider text-[#757575]">
        {label}{" "}

        <span className="text-xs normal-case text-[#9A9A9A]">
          (12 digits)
        </span>
      </label>

      <input
        type="text"
        value={digits}
        onChange={(event) =>
          handleChange(event.target.value)
        }
        placeholder="Enter 12-digit number"
        className="w-full rounded-2xl border border-[#E5E5E5] bg-white px-4 py-3 text-sm font-semibold text-[#333333] outline-none placeholder:text-[#BCBCBC] focus:border-[#FF914D] focus:ring-4 focus:ring-[#FF914D]/15"
        inputMode="numeric"
        pattern="\d{12}"
        maxLength={12}
        autoComplete="off"
      />

      <div className="mt-1 text-xs text-[#757575]">
        {digits.length}/12 · numbers only, no dashes
      </div>
    </div>
  );
});

const SelectField = memo(function SelectField({
  label,
  value,
  onChange,
  options,
  placeholder = "Select…",
}) {
  return (
    <div className="mb-5">
      <label className="mb-2 block whitespace-normal break-words text-[13px] font-bold uppercase tracking-wider text-[#757575]">
        {label}
      </label>

      <select
        value={value}
        onChange={(event) =>
          onChange(event.target.value)
        }
        className="w-full rounded-2xl border border-[#E5E5E5] bg-white px-4 py-3 text-sm font-semibold text-[#333333] outline-none focus:border-[#FF914D] focus:ring-4 focus:ring-[#FF914D]/15"
      >
        <option value="">
          {placeholder}
        </option>

        {options.map((option) => {
          const item = typeof option === "string" ? { label: option, value: option } : option;
          return <option key={item.value} value={item.value}>{item.label}</option>;
        })}
      </select>
    </div>
  );
});